import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import { signalerErreur } from '../../../components/Utils/signalerErreur';
import { FaTrashAlt, FaPlus, FaTag, FaFileImport } from "react-icons/fa";
import Select from "react-select";
import EmployeeSelect from "../../../components/Formulaire/EmployeeSelect";
import { notify } from "../../../components/Utils/Notifications";
import { Modal } from "../../../components/Utils/ModalShow";
import ImportMappingModal from "../../../components/Tools/ImportMappingModal";
import SheetSelectionModal from "../../../components/Tools/SheetSelectionModal";
import { useExcelMappingImport } from "../../../components/Tools/useExcelMappingImport";
import AmountInput from "../../../components/Utils/AmountInput";
import { normalizeEmployeeMatricule, normalizeEmployeeName } from "./excelImportUtils";

const TagModalContent = ({ onConfirm, onClose, type = "prime" }) => {
    const { t } = useTranslation();
    const [selectedTags, setSelectedTags] = useState([]);
    const [primeAmount, setPrimeAmount] = useState('');
    const [tagOptions, setTagOptions] = useState([]);

    // Adapter le texte selon le type
    const entityName = type === "advantage" ? "advantage" : "prime";

    useEffect(() => {
        apiService.getElement('/employee/categories')
            .then(response => {
                const options = response.data.categories.map(cat => ({ value: cat.id, label: cat.name }));
                setTagOptions(options);
            })
            .catch(() => notify(t('employeesPrime.tagsLoadError'), '', 3));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConfirm = () => {
        if (selectedTags.length > 0 && primeAmount) {
            onConfirm(selectedTags.map(t => t.value), parseFloat(primeAmount));
            onClose();
        } else {
            notify(entityName === "advantage" ? t('employeesPrime.selectTagAndAmountAdvantage') : t('employeesPrime.selectTagAndAmountPrime'), '', 3);
        }
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('employeesPrime.tags')}</label>
                <Select
                    isMulti
                    options={tagOptions}
                    onChange={setSelectedTags}
                    placeholder={t('employeesPrime.selectTags')}
                    className="react-select-container"
                    classNamePrefix="react-select"
                />
            </div>
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{entityName === "advantage" ? t('employeesPrime.advantageAmount') : t('employeesPrime.primeAmount')}</label>
                <input
                    type="number"
                    placeholder={t('employeesPrime.amount')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    value={primeAmount}
                    onChange={(e) => setPrimeAmount(e.target.value)}
                    step="0.01"
                    min="0"
                />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                    {t('basePrimeForm.cancel')}
                </button>
                <button
                    type="button"
                    onClick={handleConfirm}
                    className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors duration-200"
                >
                    {t('basePrimeForm.confirm')}
                </button>
            </div>
        </div>
    );
};

const EmployeesPrime = ({ 
    primeId, 
    onEmployeesPrimesChange, 
    saveChanges, 
    apiEndpoint = "/prime",
    type = "prime"
}) => {
    const { t } = useTranslation();
    const [employeesPrimes, setEmployeesPrimes] = useState([]);
    const [modifiedEmployeesPrimes, setModifiedEmployeesPrimes] = useState([]);
    const [newEmployeesPrimes, setNewEmployeesPrimes] = useState([]);
    const [removedEmployeesPrimes, setRemovedEmployeesPrimes] = useState([]);
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: 15,
        total_count: 0,
        total_pages: 0
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [nextPageOffset, setNextPageOffset] = useState(null);
    const importInputRef = useRef(null);

    // Adapter les textes selon le type
    const entityName = type === "advantage" ? "advantage" : "prime";

    useEffect(() => {
        if (primeId) {
            fetchEmployeesPrimes();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primeId, pagination.offset, pagination.limit]);

    useEffect(() => {
        if (onEmployeesPrimesChange) {
            onEmployeesPrimesChange({
                employeesPrimes,
                newEmployeesPrimes,
                modifiedEmployeesPrimes,
                removedEmployeesPrimes
            });
        }
    }, [employeesPrimes, newEmployeesPrimes, modifiedEmployeesPrimes, removedEmployeesPrimes, onEmployeesPrimesChange]);

    const fetchEmployeesPrimes = async () => {
        if (!primeId) return;
        try {
            // Utiliser l'endpoint approprié selon le type
            const response = await apiService.getElement(`${apiEndpoint}/${primeId}/employees?offset=${pagination.offset}&limit=${pagination.limit}`);
            if (response.data.employees) {
                setEmployeesPrimes(response.data.employees);
                setPagination(response.data.pagination)
                setNewEmployeesPrimes([]);
                setModifiedEmployeesPrimes([]);
                setRemovedEmployeesPrimes([]);
            } else {
                setErrorMessage(entityName === "advantage" ? t('employeesPrime.fetchFailedAdvantage') : t('employeesPrime.fetchFailedPrime'));
            }
        } catch (error) {
            signalerErreur(error, 'EmployeesPrime.fetchEmployeesPrimes');
            setErrorMessage(entityName === "advantage" ? t('employeesPrime.fetchFailedAdvantage') : t('employeesPrime.fetchFailedPrime'));
        }
    };

    const handleAddByTag = async (tagIds, amount) => {
        try {
            const response = await apiService.getElement(`/employees?filter_by_category_ids=${tagIds.join(',')}`);
            const employeesFromTags = response.data.employees;

            if (!employeesFromTags || employeesFromTags.length === 0) {
                notify(t('employeesPrime.noEmployeeForTags'), '', 1);
                return;
            }
            const employeesMap = new Map(employeesPrimes.map(ep => [ep.id_employee, ep]));
            const newEmployeesMap = new Map(newEmployeesPrimes.map(ep => [ep.id_employee, ep]));
            const modifiedEmployeesMap = new Map(modifiedEmployeesPrimes.map(ep => [ep.id_employee, ep]));

            employeesFromTags.forEach(emp => {
                const employeePrimeData = {
                    id_employee: emp.id,
                    employee_name: emp.name,
                    prime: amount,
                };

                const existingEmployee = employeesMap.get(emp.id);

                if (existingEmployee) {
                    employeePrimeData.id = existingEmployee.id;
                    if (!existingEmployee.isNew) {                        
                        modifiedEmployeesMap.set(emp.id, employeePrimeData);
                    } else {                        
                        newEmployeesMap.set(emp.id, employeePrimeData);
                    }
                } else {                    
                    employeePrimeData.isNew = true;
                    newEmployeesMap.set(emp.id, employeePrimeData);
                }                                
                employeesMap.set(emp.id, employeePrimeData);
            });            
            setEmployeesPrimes(Array.from(employeesMap.values()));
            setNewEmployeesPrimes(Array.from(newEmployeesMap.values()));
            setModifiedEmployeesPrimes(Array.from(modifiedEmployeesMap.values()));
            notify(t('employeesPrime.employeesAddedViaTags', { count: employeesFromTags.length }), '', 2);
        } catch (error) {
            signalerErreur(error, 'EmployeesPrime.handleAddByTag');
            notify(t('employeesPrime.fetchByTagError'), '', 3);
        }
    };

    const handlePageChange = (newOffset) => {
        const hasChanges = newEmployeesPrimes.length > 0 || modifiedEmployeesPrimes.length > 0 || removedEmployeesPrimes.length > 0;
        if (hasChanges) {
            setNextPageOffset(newOffset);
            setIsModalOpen(true);
        } else {
            setPagination(prev => ({ ...prev, offset: newOffset }));
        }
    };

    const proceedToNextPage = () => {
        if (nextPageOffset !== null) {
            setPagination(prev => ({ ...prev, offset: nextPageOffset }));
            setNextPageOffset(null);
        }
        setIsModalOpen(false);
    };

    const handleConfirmSaveChanges = async () => {
        if (saveChanges) {
            await saveChanges();
        }
        proceedToNextPage();
    };

    const handleDiscardChanges = () => {        
        setNewEmployeesPrimes([]);
        setModifiedEmployeesPrimes([]);
        setRemovedEmployeesPrimes([]);
        proceedToNextPage();
    };

    const handleAddNewLine = () => {
        const newEmployeePrime = {
            id_employee: '',
            employee_name: '',
            prime: '',
            isNew: true
        };
        setEmployeesPrimes(prev => [...prev, newEmployeePrime]);
        setNewEmployeesPrimes(prev => [...prev, newEmployeePrime]);
    };

    const handleImportClick = () => {
        if (importInputRef.current) {
            importInputRef.current.click();
        }
    };

    const parseAmount = (value) => {
        if (typeof value === "number") return Number.isFinite(value) ? value : null;
        if (value === null || value === undefined) return null;
        const cleaned = String(value).replace(/\s/g, "").replace(",", ".");
        const parsed = parseFloat(cleaned);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const applyImportedRows = async (importedRows) => {
        const employeesResponse = await apiService.getElement('/employees?offset=0&limit=5000');
        const employees = employeesResponse.data?.employees || [];
        const employeeByName = new Map();
        const employeeByMatricule = new Map();

        employees.forEach((emp) => {
            const key = normalizeEmployeeName(emp.name);
            if (!employeeByName.has(key)) {
                employeeByName.set(key, []);
            }
            employeeByName.get(key).push(emp);

            const matriculeKey = normalizeEmployeeMatricule(emp.matricule);
            if (matriculeKey && !employeeByMatricule.has(matriculeKey)) {
                employeeByMatricule.set(matriculeKey, emp);
            }
        });

        const currentEmployeesMap = new Map(employeesPrimes.map((ep) => [ep.id_employee, ep]));
        const newEmployeesMap = new Map(newEmployeesPrimes.map((ep) => [ep.id_employee, ep]));
        const modifiedEmployeesMap = new Map(modifiedEmployeesPrimes.map((ep) => [ep.id_employee, ep]));
        const unresolved = [];

        importedRows.forEach((row) => {
            const matriculeKey = normalizeEmployeeMatricule(row.matricule);
            const nameKey = normalizeEmployeeName(row.employeeName);
            const matches = employeeByName.get(nameKey) || [];
            const selectedEmployee = (matriculeKey && employeeByMatricule.get(matriculeKey)) || matches[0];

            if (!selectedEmployee) {
                unresolved.push(row.matricule || row.employeeName || t('deductionForm.rowWithoutIdentifier'));
                return;
            }

            const employeePrimeData = {
                id_employee: selectedEmployee.id,
                employee_name: selectedEmployee.name,
                prime: row.amount,
            };

            const existing = currentEmployeesMap.get(selectedEmployee.id);
            if (existing?.id) {
                employeePrimeData.id = existing.id;
            }

            if (existing?.isNew || newEmployeesMap.has(selectedEmployee.id)) {
                employeePrimeData.isNew = true;
                newEmployeesMap.set(selectedEmployee.id, employeePrimeData);
            } else if (existing) {
                modifiedEmployeesMap.set(selectedEmployee.id, employeePrimeData);
            } else {
                employeePrimeData.isNew = true;
                newEmployeesMap.set(selectedEmployee.id, employeePrimeData);
            }

            currentEmployeesMap.set(selectedEmployee.id, employeePrimeData);
        });

        setEmployeesPrimes(Array.from(currentEmployeesMap.values()));
        setNewEmployeesPrimes(Array.from(newEmployeesMap.values()));
        setModifiedEmployeesPrimes(Array.from(modifiedEmployeesMap.values()));

        const importedCount = importedRows.length - unresolved.length;
        if (importedCount > 0) {
            notify(t('employeesPrime.importedCount', { count: importedCount }), '', 2);
        }
        if (unresolved.length > 0) {
            notify(t('deductionForm.unresolvedCount', { count: unresolved.length, list: unresolved.slice(0, 5).join(', ') + (unresolved.length > 5 ? '...' : '') }), '', 4);
        }
        if (importedCount === 0 && unresolved.length > 0) {
            notify(t('deductionForm.noRowsImported'), '', 3);
        }
    };

    const excelImport = useExcelMappingImport({
        notify,
        modelFields: [
            { name: "matricule", string: t('deductionForm.matricule') },
            { name: "employeeName", string: t('deductionForm.employeeName') },
            { name: "amount", string: t('deductionForm.amountField') },
        ],
        templateHeaders: [t('deductionForm.matricule'), t('deductionForm.employeeName'), t('deductionForm.amountField')],
        templateSheetName: "Import Prime",
        templateFilename: `modele_import_${entityName}.xlsx`,
        mapMappedRow: (row) => ({
            employeeName: String(row.employeeName || "").trim(),
            matricule: String(row.matricule || "").trim(),
            amount: parseAmount(row.amount),
        }),
        isMappedRowValid: (row) => (row.employeeName || row.matricule) && row.amount !== null,
        onImportRows: applyImportedRows,
    });

    const handleEmployeeChange = (index, option) => {
        if (!option) {
            // Supprimer la ligne si aucun employé sélectionné
            handleRemove(index);
            return;
        }
        const updatedEmployeesPrimes = [...employeesPrimes];
        updatedEmployeesPrimes[index].id_employee = parseInt(option.value);
        updatedEmployeesPrimes[index].employee_name = option.label || '';
        setEmployeesPrimes(updatedEmployeesPrimes);
        setModifiedEmployeesPrimes(prev => {
            const filtered = prev.filter(item => item !== employeesPrimes[index]);
            return [...filtered, updatedEmployeesPrimes[index]];
        });
    };

    const handlePrimeChange = (index, value) => {
        const updatedEmployeesPrimes = [...employeesPrimes];
        updatedEmployeesPrimes[index].prime = value ? parseFloat(value) : '';
        setEmployeesPrimes(updatedEmployeesPrimes);        
         setModifiedEmployeesPrimes(prev => {
            const existing = prev.find(item => item.id_employee === updatedEmployeesPrimes[index].id_employee);
            if (existing) {
                return prev;
            }
            return [...prev, updatedEmployeesPrimes[index]];
        });
    };

    const handleRemove = (index) => {
        const employeeToRemove = employeesPrimes[index];        
        if (employeeToRemove.isNew) {        
            setNewEmployeesPrimes(prev => prev.filter(emp => emp !== employeeToRemove));
        } else {            
            setRemovedEmployeesPrimes(prev => [...prev, employeeToRemove]);
        }
        setEmployeesPrimes(prev => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    };

    return (
        <div className="space-y-4">
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={t('employeesPrime.unsavedChangesTitle')}
                footer={
                    <>
                        <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200">
                            {t('basePrimeForm.cancel')}
                        </button>
                        <button type="button" onClick={handleDiscardChanges} className="px-4 py-2 text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg transition-colors duration-200">
                            {t('employeesPrime.discardAndContinue')}
                        </button>
                        <button type="button" onClick={handleConfirmSaveChanges} className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors duration-200">
                            {t('employeesPrime.saveAndContinue')}
                        </button>
                    </>
                }
            >
                <p className="text-gray-600">{t('employeesPrime.unsavedChangesBody')}</p>
            </Modal>

            <Modal
                isOpen={isTagModalOpen}
                onClose={() => setIsTagModalOpen(false)}
                title={type === "advantage" ? t('employeesPrime.addByTagTitleAdvantage') : t('employeesPrime.addByTagTitlePrime')}
            >
                <TagModalContent 
                    onConfirm={handleAddByTag} 
                    onClose={() => setIsTagModalOpen(false)} 
                    type={type}
                />
            </Modal>
            <SheetSelectionModal
                isOpen={excelImport.showSheetSelection}
                sheetNames={excelImport.sheetNames}
                onSelect={excelImport.handleSheetSelect}
                onCancel={excelImport.handleCancelSheetSelection}
            />
            {excelImport.showMapping && (
                <ImportMappingModal
                    columns={excelImport.fileColumns}
                    modelFields={excelImport.modelFields}
                    onDownloadTemplate={excelImport.handleDownloadTemplate}
                    onConfirm={excelImport.handleConfirmMapping}
                    onTest={excelImport.handleTestMapping}
                    onCancel={excelImport.handleCancelMapping}
                />
            )}

            {/* Actions Header */}
            <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                    {employeesPrimes.length > 0 && (
                        <span>{t('employeesPrime.assignedCount', { count: employeesPrimes.length })}</span>
                    )}
                </div>
                <div className="flex gap-3">
                    <button
                        type="button"
                        className="flex items-center gap-2 px-4 py-2 text-[--color-primary]  hover:bg-[--bg-color] rounded-lg transition-colors duration-200"
                        onClick={handleAddNewLine}
                    >
                        <FaPlus size={14} />
                        {t('employeesPrime.addEmployee')}
                    </button>
                    <button
                        type="button"
                        className="flex items-center gap-2 px-4 py-2 text-[--color-orange] hover:bg-orange-50 rounded-lg transition-colors duration-200"
                        onClick={() => setIsTagModalOpen(true)}
                    >
                        <FaTag size={14} />
                        {t('employeesPrime.addByTag')}
                    </button>
                    <button
                        type="button"
                        className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        onClick={handleImportClick}
                    >
                        <FaFileImport size={14} />
                        {t('deductionForm.importExcel')}
                    </button>
                    <input
                        ref={importInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                        onChange={excelImport.handleImportFileChange}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-visible">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">{t('deductionForm.employee')}</th>
                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">{t('deductionForm.amountField')}</th>
                                <th className="px-6 py-4 text-center text-sm font-medium text-gray-700">{t('deductionForm.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {employeesPrimes.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center">
                                            <div className="bg-gray-100 rounded-full p-3 mb-3">
                                                <FaPlus className="text-gray-400" size={24} />
                                            </div>
                                            <p className="text-lg font-medium mb-1">{t('deductionForm.noEmployeeAssigned')}</p>
                                            <p className="text-sm">{t('employeesPrime.noEmployeeAssignedHelp')}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                employeesPrimes.map((emp, index) => {
                                    const emp_select = emp.id_employee ? { value: emp.id_employee, label: emp.employee_name } : null;
                                    return (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="px-6 py-4 relative">
                                                <EmployeeSelect
                                                    value={emp_select}
                                                    onChange={(option) => handleEmployeeChange(index, option)}
                                                    isDisabled={false}
                                                    placeholder={t('employeesPrime.selectEmployee')}
                                                    className="min-w-0"
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                    }}
                                                />
                                            </td>                                    
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <AmountInput
                                                        value={emp.prime}
                                                        onChange={(newValue) => handlePrimeChange(index, newValue)}
                                                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemove(index)}
                                                    className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors duration-200"
                                                    title={t('employeeDeduction.removeLine')}
                                                >
                                                    <FaTrashAlt size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Error Message */}
            {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">{errorMessage}</p>
                </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
                <div className="flex justify-between items-center pt-4">
                    <div className="text-sm text-gray-600">
                        {t('employeesPrime.showingResults', {
                            from: pagination.offset + 1,
                            to: Math.min(pagination.offset + pagination.limit, pagination.total_count),
                            total: pagination.total_count
                        })}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            disabled={pagination.offset === 0}
                            onClick={() => handlePageChange(pagination.offset - pagination.limit)}
                        >
                            {t('employeesPrime.previous')}
                        </button>
                        <span className="px-3 py-2 text-sm text-gray-700">
                            {t('employeesPrime.pageOf', { page: Math.floor(pagination.offset / pagination.limit) + 1, total: pagination.total_pages })}
                        </span>
                        <button
                            type="button"
                            className="px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            disabled={pagination.offset + pagination.limit >= pagination.total_count}
                            onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                        >
                            {t('employeesPrime.next')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeesPrime;
